// Copyright 2025-2026 NXGN Management, LLC. All Rights Reserved.
import { useMemo } from 'react'
import {
  ColumnDef,
  Table,
  Typography,
  SortingState,
  StateMessage,
} from '@teamnine/pcj-ui-v2'
import { List } from '@/utils/material-icons'
import { useNavigate } from 'react-router-dom'

import { Customer as ICustomer } from '@/services/customers'
import { Tenant as ITenant } from '@/services/practices'
import LinkButton from '@/app/shared/components/LinkButton'

const PAGE_SIZE = 30

type ArchivedPracticesViewProps = {
  archivedPractices: ITenant[]
  currentCustomer?: ICustomer
  isLoading: boolean
  searchString: string
  archivedSortState: SortingState
  archivedPracticesPageIndex: number
  onSortingChange: (next: SortingState) => void
  onPageChange: (pageIndex: number) => void
  onSearch: (val: string[]) => void
}

export default function ArchivedPracticesView({
  archivedPractices,
  currentCustomer,
  isLoading,
  searchString,
  archivedSortState,
  archivedPracticesPageIndex,
  onSortingChange,
  onPageChange,
  onSearch,
}: ArchivedPracticesViewProps) {
  const navigate = useNavigate()

  const filteredArchivedPractices = useMemo(() => {
    const filtered = archivedPractices.filter((practice) => {
      if (!searchString) return true
      const search = searchString.trim().toLowerCase()
      return (
        practice.name.toLowerCase().includes(search) ||
        practice.subdomain.toLowerCase().includes(search) ||
        practice.auth0OrgId.toLowerCase().includes(search) ||
        practice.enterprisePracticeId.toLowerCase().startsWith(search) ||
        practice.siteId.toLowerCase().startsWith(search)
      )
    })

    if (!archivedSortState.length) return filtered

    const { id, desc } = archivedSortState[0]
    return [...filtered].sort((a, b) => {
      let result = 0
      if (id === 'practiceName') {
        result = a.name.localeCompare(b.name, undefined, {
          sensitivity: 'base',
        })
      } else if (id === 'subdomain') {
        result = a.subdomain.localeCompare(b.subdomain, undefined, {
          sensitivity: 'base',
        })
      } else if (id === 'auth0OrgId') {
        result = a.auth0OrgId.localeCompare(b.auth0OrgId, undefined, {
          sensitivity: 'base',
        })
      } else if (id === 'enterprisePracticeId') {
        result = a.enterprisePracticeId.localeCompare(
          b.enterprisePracticeId,
          undefined,
          {
            numeric: true,
            sensitivity: 'base',
          }
        )
      } else if (id === 'siteId') {
        result = a.siteId.localeCompare(b.siteId, undefined, {
          numeric: true,
          sensitivity: 'base',
        })
      }
      return desc ? -result : result
    })
  }, [archivedPractices, archivedSortState, searchString])

  const pagedArchivedPractices = useMemo(() => {
    const start = archivedPracticesPageIndex * PAGE_SIZE
    return filteredArchivedPractices.slice(start, start + PAGE_SIZE)
  }, [filteredArchivedPractices, archivedPracticesPageIndex])

  const practiceColumns: ColumnDef<ITenant>[] = useMemo(
    () => [
      {
        id: 'practiceName',
        header: 'Name',
        accessorKey: 'name' as const,
        enableSorting: true,
        cell: ({ row }) => (
          <LinkButton
            label={row.original.name}
            onClick={() =>
              navigate(
                `/customer-management/${currentCustomer?.id}/practices/${row.original.id}`
              )
            }
          />
        ),
      },
      {
        id: 'enterprisePracticeId',
        header: 'Practice ID',
        accessorKey: 'enterprisePracticeId' as const,
        enableSorting: true,
        cell: ({ row }) => (
          <Typography>{row.original.enterprisePracticeId || 'N/A'}</Typography>
        ),
      },
      {
        id: 'siteId',
        header: 'Site ID',
        accessorKey: 'siteId' as const,
        enableSorting: true,
        cell: ({ row }) => (
          <Typography>{row.original.siteId || 'N/A'}</Typography>
        ),
      },
      {
        id: 'subdomain',
        header: 'Subdomain',
        accessorKey: 'subdomain' as const,
        enableSorting: true,
      },
      {
        id: 'auth0OrgId',
        header: 'Auth0 Org ID',
        accessorKey: 'auth0OrgId' as const,
        enableSorting: true,
      },
    ],
    [navigate, currentCustomer]
  )

  if (!isLoading && archivedPractices.length === 0) {
    return <StateMessage title="There are no archived practices." Icon={List} />
  }

  return (
    <Table<ITenant>
      borderless
      data={pagedArchivedPractices}
      totalCount={filteredArchivedPractices.length}
      isLoading={isLoading}
      columns={practiceColumns}
      sorting={{
        state: archivedSortState,
        onSortingChange: (next) => {
          onSortingChange(next)
          onPageChange(0)
        },
      }}
      search={{
        type: 'single',
        onSearch,
        placeholder: 'Enter practice name',
        label: 'Search practice',
        width: 400,
      }}
      pagination={{
        mode: 'paged',
        pageIndex: archivedPracticesPageIndex,
        pageSize: PAGE_SIZE,
        totalCount: filteredArchivedPractices.length,
        onPageChange: ({ pageIndex }) => {
          onPageChange(pageIndex)
        },
      }}
      empty={{
        title: 'There are no archived practices.',
        Icon: List,
      }}
    />
  )
}
